import React, { useEffect, useState } from "react";

// Import Lucide icons
import {
  Users,
  User,
  Mail,
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  Shield,
  BarChart3,
  MessageSquare,
  RefreshCw
} from "lucide-react";

import api from "../../api";
import { Pagination, Stack } from "@mui/material";
import { Loader } from "./loader";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { Form, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import './SupportOnline.css'

function AdminSupportOnline({ adminType }) {
  const adminStore = useSelector((state) => state.adminReducer);

  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [onlineStatus, setOnlineStatus] = useState(false);
  const [currentSupport, setCurrentSupport] = useState({});
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  const getSupport = async () => {
    try {
      setLoader(true);
      let withstats =
        adminStore?.admin?.admin?.email == "alex@waqarzaka.net" ||
        adminStore?.admin?.admin?.email == "malaika@waqarzaka.net" ||
        adminStore?.admin?.admin?.email == "mike@waqarzaka.net";
      
      const res = await api.getSupportAdmin(withstats);
      if (res?.success) {
        setData(res.support);
        let currentS = res.support.find(
          (d) => d?._id == adminStore?.admin?.admin?._id
        );
        setCurrentSupport(currentS);
        setLastRefreshed(new Date());
      }
      setLoader(false);
    } catch (error) {
      console.log(error?.message);
      setLoader(false);
    }
  };

  useEffect(() => {
    getSupport();
  }, []);

  const refreshData = () => {
    getSupport();
  };

  const formatTime = (date) => {
    return moment(date).format("h:mm:ss A");
  };

  return (
    <>
      <div className="admin-support-container-fluid admin-support-page">
        <div className="admin-support-row">
          <div className="admin-support-col">
            <div className="admin-support-header">
              <div className="admin-support-header-content">
                <div className="admin-support-title-section">
                  <div className="admin-support-title-icon-wrapper">
                    <Users size={24} className="admin-support-title-icon" />
                  </div>
                  <div>
                    <h1 className="admin-support-title">Support Team Dashboard</h1>
                    <div className="admin-support-subtitle">
                      Monitor support team activity and performance
                    </div>
                  </div>
                  <div className="admin-support-count-badge">
                    <span className="admin-support-count">{data?.length || 0}</span>
                    <span className="admin-support-count-label">Active Members</span>
                  </div>
                </div>
                
                <div className="admin-support-header-actions">
                  <button 
                    className="admin-support-refresh-button"
                    onClick={refreshData}
                    disabled={loader}
                  >
                    <RefreshCw size={18} className={loader ? "admin-support-spinner" : ""} />
                    {loader ? "Refreshing..." : "Refresh"}
                  </button>
                  <div className="admin-support-last-updated">
                    <Clock size={14} />
                    Last updated: {formatTime(lastRefreshed)}
                  </div>
                </div>
              </div>
              
              <div className="admin-support-stats-container">
                <div className="admin-support-stat-card">
                  <div className="admin-support-stat-icon admin-support-stat-icon-online">
                    <Activity size={20} />
                  </div>
                  <div className="admin-support-stat-content">
                    <span className="admin-support-stat-label">Online Now</span>
                    <span className="admin-support-stat-value">
                      {data.filter(item => {
                        const THREE_MINUTES = 3 * 60 * 1000;
                        return item?.lastActiveAt && 
                          Date.now() - new Date(item.lastActiveAt).getTime() < THREE_MINUTES;
                      }).length}
                    </span>
                  </div>
                </div>
                
                <div className="admin-support-stat-card">
                  <div className="admin-support-stat-icon admin-support-stat-icon-processing">
                    <MessageSquare size={20} />
                  </div>
                  <div className="admin-support-stat-content">
                    <span className="admin-support-stat-label">Processing Tickets</span>
                    <span className="admin-support-stat-value">
                      {data.reduce((sum, item) => sum + (item.processedCount || 0), 0)}
                    </span>
                  </div>
                </div>
                
                <div className="admin-support-stat-card">
                  <div className="admin-support-stat-icon admin-support-stat-icon-closed">
                    <CheckCircle size={20} />
                  </div>
                  <div className="admin-support-stat-content">
                    <span className="admin-support-stat-label">Tickets Closed</span>
                    <span className="admin-support-stat-value">
                      {data.reduce((sum, item) => sum + (item.closedCount || 0), 0)}
                    </span>
                  </div>
                </div>
                
                <div className="admin-support-stat-card">
                  <div className="admin-support-stat-icon admin-support-stat-icon-admin">
                    <Shield size={20} />
                  </div>
                  <div className="admin-support-stat-content">
                    <span className="admin-support-stat-label">Admin Users</span>
                    <span className="admin-support-stat-value">
                      {data.filter(item => item.role === "sackAdmin755").length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="admin-support-table-container">
              {adminStore?.admin?.admin?.email == "alex@waqarzaka.net" ||
               adminStore?.admin?.admin?.email == "malaika@waqarzaka.net" ||
               adminStore?.admin?.admin?.email == "mike@waqarzaka.net" ? (
                <TableWithStats data={data} />
              ) : (
                <Table data={data} />
              )}
            </div>
          </div>
        </div>
      </div>
      
      {loader && <Loader />}
    </>
  );
}

const Table = ({ data }) => {
  const navigate = useNavigate();
  const THREE_MINUTES = 3 * 60 * 1000;

  const isOnline = (lastActiveAt) => {
    if (lastActiveAt) {
      return Date.now() - new Date(lastActiveAt).getTime() < THREE_MINUTES;
    } else {
      return false;
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    let dateTemp = moment(date).format("MMMM Do YYYY, h:mm:ss a");
    return dateTemp;
  };

  return (
    <div className="admin-support-card">
      <div className="admin-support-card-header">
        <div className="admin-support-card-title">
          <h2>Support Team Members</h2>
          <span className="admin-support-card-subtitle">
            Real-time status and activity monitoring
          </span>
        </div>
      </div>
      
      <div className="admin-support-table-wrapper">
        {data.length === 0 ? (
          <div className="admin-support-empty-state">
            <div className="admin-support-empty-state-icon">
              <Users size={48} />
            </div>
            <h3 className="admin-support-empty-state-title">No Support Members Found</h3>
            <p className="admin-support-empty-state-description">
              There are currently no support team members in the system.
            </p>
          </div>
        ) : (
          <table className="admin-support-table">
            <thead className="admin-support-table-header">
              <tr>
                <th className="admin-support-table-th">
                  <div className="admin-support-table-header-cell">
                    <User size={30} />
                    <span>Name</span>
                  </div>
                </th>
                <th className="admin-support-table-th">
                  <div className="admin-support-table-header-cell">
                    <Mail size={30} />
                    <span>Email</span>
                  </div>
                </th>
                <th className="admin-support-table-th">
                  <div className="admin-support-table-header-cell">
                    <Clock size={30} />
                    <span>Last Active</span>
                  </div>
                </th>
                <th className="admin-support-table-th">
                  <div className="admin-support-table-header-cell">
                    <Activity size={30} />
                    <span>Status</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="admin-support-table-body">
              {data.map((item, key) => (
                <tr key={key} className="admin-support-table-row">
                  <td className="admin-support-table-td">
                    <div className="admin-support-user-info">
                      <div className="admin-support-user-avatar">
                        {item?.name?.charAt(0) || "U"}
                      </div>
                      <div className="admin-support-user-details">
                        <span className="admin-support-user-name">
                          {item?.name || "N/A"}
                        </span>
                        {item?.role === "sackAdmin755" && (
                          <span className="admin-support-user-role">
                            <Shield size={12} />
                            Admin
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="admin-support-table-td">
                    <div className="admin-support-email-cell">
                      <span className="admin-support-email">
                        {item?.email}
                      </span>
                    </div>
                  </td>
                  <td className="admin-support-table-td">
                    <div className="admin-support-date-cell">
                      <Clock size={14} className="admin-support-date-icon" />
                      <span>{formatDate(item?.lastActiveAt)}</span>
                    </div>
                  </td>
                  <td className="admin-support-table-td">
                    <div className={`admin-support-status ${isOnline(item?.lastActiveAt) ? 'admin-support-status-online' : 'admin-support-status-offline'}`}>
                      <div className="admin-support-status-indicator"></div>
                      <span className="admin-support-status-text">
                        {isOnline(item?.lastActiveAt) ? "Online" : "Offline"}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const TableWithStats = ({ data }) => {
  const THREE_MINUTES = 3 * 60 * 1000;

  const isOnline = (lastActiveAt) => {
    if (lastActiveAt) {
      return Date.now() - new Date(lastActiveAt).getTime() < THREE_MINUTES;
    } else {
      return false;
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    let dateTemp = moment(date).format("MMMM Do YYYY, h:mm:ss a");
    return dateTemp;
  };

  return (
    <div className="admin-support-card">
      <div className="admin-support-card-header">
        <div className="admin-support-card-title">
          <h2>Support Team Performance</h2>
          <span className="admin-support-card-subtitle">
            Detailed statistics and activity monitoring
          </span>
        </div>
      </div>
      
      <div className="admin-support-table-wrapper">
        {data.length === 0 ? (
          <div className="admin-support-empty-state">
            <div className="admin-support-empty-state-icon">
              <BarChart3 size={48} />
            </div>
            <h3 className="admin-support-empty-state-title">No Support Data Available</h3>
            <p className="admin-support-empty-state-description">
              There are currently no support team members in the system.
            </p>
          </div>
        ) : (
          <table className="admin-support-table">
            <thead className="admin-support-table-header">
              <tr>
                <th className="admin-support-table-th">
                  <div className="admin-support-table-header-cell">
                    <User size={16} />
                    <span>Name</span>
                  </div>
                </th>
                <th className="admin-support-table-th">
                  <div className="admin-support-table-header-cell">
                    <Mail size={16} />
                    <span>Email</span>
                  </div>
                </th>
                <th className="admin-support-table-th">
                  <div className="admin-support-table-header-cell">
                    <MessageSquare size={16} />
                    <span>Processing</span>
                  </div>
                </th>
                <th className="admin-support-table-th">
                  <div className="admin-support-table-header-cell">
                    <CheckCircle size={16} />
                    <span>Closed</span>
                  </div>
                </th>
                <th className="admin-support-table-th">
                  <div className="admin-support-table-header-cell">
                    <Clock size={16} />
                    <span>Last Active</span>
                  </div>
                </th>
                <th className="admin-support-table-th">
                  <div className="admin-support-table-header-cell">
                    <Activity size={16} />
                    <span>Status</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="admin-support-table-body">
              {data.map((item, key) => (
                <tr key={key} className="admin-support-table-row">
                  <td className="admin-support-table-td">
                    <div className="admin-support-user-info">
                      <div className="admin-support-user-avatar">
                        {item?.name?.charAt(0) || "U"}
                      </div>
                      <div className="admin-support-user-details">
                        <span className="admin-support-user-name">
                          {item?.name || "N/A"}
                        </span>
                        {item?.role === "sackAdmin755" && (
                          <span className="admin-support-user-role">
                            <Shield size={12} />
                            Admin
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="admin-support-table-td">
                    <div className="admin-support-email-cell">
                      <span className="admin-support-email">
                        {item?.email}
                      </span>
                    </div>
                  </td>
                  <td className="admin-support-table-td">
                    <div className="admin-support-count-cell">
                      <div className="admin-support-count-badge admin-support-count-badge-processing">
                        <span>{item?.processedCount || 0}</span>
                      </div>
                    </div>
                  </td>
                  <td className="admin-support-table-td">
                    <div className="admin-support-count-cell">
                      <div className="admin-support-count-badge admin-support-count-badge-closed">
                        <span>{item?.closedCount || 0}</span>
                      </div>
                    </div>
                  </td>
                  <td className="admin-support-table-td">
                    <div className="admin-support-date-cell">
                      <Clock size={24} className="admin-support-date-icon" />
                      <span>{formatDate(item?.lastActiveAt)}</span>
                    </div>
                  </td>
                  <td className="admin-support-table-td">
                    <div className={`admin-support-status ${isOnline(item?.lastActiveAt) ? 'admin-support-status-online' : 'admin-support-status-offline'}`}>
                      <div className="admin-support-status-indicator"></div>
                      <span className="admin-support-status-text">
                        {isOnline(item?.lastActiveAt) ? "Online" : "Offline"}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminSupportOnline;